import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { hrRequestApi } from '../../api/hr-requests';
import { HrRequest } from '../../types/hr-requests';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { RecordDetailModal } from '../../components/RecordDetailModal';
import { ReturnAction } from '../../components/ReturnAction';
import { useToast } from '../../components/Toast';

const TYPE_LABELS: Record<string, string> = {
  confirmation_letter: 'Employment Confirmation Letter', salary_confirmation: 'Salary Confirmation',
  id_card_replacement: 'ID Card Replacement', contract_amendment: 'Contract Amendment',
  profile_update: 'Profile Update', training: 'Training', other: 'Other',
};

export function HrRequestsPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [viewingRequest, setViewingRequest] = useState<HrRequest | null>(null);
  const pendingQuery = useQuery({ queryKey: ['hr-requests-pending'], queryFn: () => hrRequestApi.pending() });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['hr-requests-pending'] });
  const resolveMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => hrRequestApi.resolve(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });
  const completeMutation = useMutation({
    mutationFn: (id: number) => hrRequestApi.complete(id),
    onSuccess: () => { showSuccess('Marked completed.'); invalidate(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <h1>HR Requests</h1>
      {pendingQuery.data && (
        <DataTable
          rows={pendingQuery.data.data}
          rowKey={(r) => r.id}
          emptyMessage="Nothing pending."
          columns={[
            { header: 'Employee', render: (r) => r.employee_name },
            { header: 'Type', render: (r) => TYPE_LABELS[r.request_type] ?? r.request_type },
            { header: 'Details', render: (r) => r.details ?? '-' },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} /> },
            {
              header: '',
              render: (r) => (
                <span className="row-actions">
                  <button className="link-button" onClick={() => setViewingRequest(r)}>View</button>
                  {(r.status === 'submitted' || r.status === 'under_review') && (
                    <>
                      <button className="link-button" onClick={() => resolveMutation.mutate({ id: r.id, decision: 'approved' })}>Approve</button>
                      <button className="link-button" onClick={() => resolveMutation.mutate({ id: r.id, decision: 'rejected' })}>Reject</button>
                      <ReturnAction onReturn={(comment) => hrRequestApi.return(r.id, comment)} queryKeysToInvalidate={[['hr-requests-pending']]} />
                    </>
                  )}
                  {r.status === 'approved' && <button className="link-button" onClick={() => completeMutation.mutate(r.id)}>Mark Completed</button>}
                </span>
              ),
            },
          ]}
        />
      )}

      {viewingRequest && (
        <RecordDetailModal
          title={`HR Request - ${viewingRequest.employee_name ?? ''}`}
          onClose={() => setViewingRequest(null)}
          fields={[
            { label: 'Type', value: TYPE_LABELS[viewingRequest.request_type] ?? viewingRequest.request_type },
            { label: 'Status', value: <StatusBadge status={viewingRequest.status} /> },
            { label: 'Details', value: viewingRequest.details, fullWidth: true },
            { label: 'Return Comment', value: viewingRequest.return_comment, fullWidth: true },
            { label: 'Resolution Notes', value: viewingRequest.resolution_notes, fullWidth: true },
          ]}
        >
          {viewingRequest.proposed_changes && (
            <div style={{ marginTop: '1em' }}>
              <div className="record-detail-label">Proposed Profile Changes</div>
              <table className="data-table">
                <thead><tr><th>Field</th><th>Current</th><th>Proposed</th></tr></thead>
                <tbody>
                  {Object.entries(viewingRequest.proposed_changes).map(([field, proposedValue]) => (
                    <tr key={field}>
                      <td>{field}</td>
                      <td>{viewingRequest.current_values?.[field] ?? '-'}</td>
                      <td>{proposedValue ?? '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </RecordDetailModal>
      )}
    </div>
  );
}
